import { useState } from 'react';
import type { ReactElement } from 'react';
import { formatDate, formatNumber } from '@xdocs/i18n';
import type { CreditReason } from '@xdocs/schemas';
import { Badge, Button, DataTable, cn, useI18n } from '@xdocs/ui';
import type { ColumnDef } from '@xdocs/ui';
import { useCreditsBalance } from '../../../features/notifications/useCreditsBalance.js';
import { BUY_CREDITS_SECTION_ID } from './BuyCreditsGrid.js';
import { CreditsInfoModal } from './CreditsInfoModal.js';
import type { CreditLedgerRow } from './useCreditLedger.js';
import { useCreditLedger } from './useCreditLedger.js';
import { useSettingsBillingT } from './useSettingsBillingT.js';
import styles from './CreditsSection.module.css';

const REASONS: readonly CreditReason[] = [
  'action_debit',
  'policy_refund',
  'purchase',
  'admin_grant',
  'share_approved',
  'share_public_reward',
  'referral_reward',
  'promo_bonus',
  'signup_grant',
];

/** F19 credits section: live balance pill + ledger `<DataTable>` with a
 * client-computed running balance column. */
export function CreditsSection(): ReactElement {
  const { locale } = useI18n();
  const { t } = useSettingsBillingT();
  const balance = useCreditsBalance();
  const ledger = useCreditLedger(balance.balance);
  const [infoOpen, setInfoOpen] = useState(false);

  function handleBuyCredits(): void {
    setInfoOpen(false);
    document.getElementById(BUY_CREDITS_SECTION_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  const columns: ColumnDef<CreditLedgerRow>[] = [
    {
      id: 'createdAt',
      header: t('settingsBilling:credits.ledger.column.date'),
      sortable: true,
      primary: true,
      cell: (row) => <span className={cn(styles['dateCell'])}>{formatDate(locale, row.createdAt, { dateStyle: 'medium', timeStyle: 'short' })}</span>,
    },
    {
      id: 'reason',
      header: t('settingsBilling:credits.ledger.column.reason'),
      cell: (row) => (REASONS.includes(row.reason) ? t(`settingsBilling:credits.ledger.reason.${row.reason}`) : row.reason),
    },
    {
      id: 'delta',
      header: t('settingsBilling:credits.ledger.column.delta'),
      cell: (row) => (
        <span className={cn(row.delta >= 0 ? styles['positive'] : styles['negative'])}>
          {row.delta >= 0 ? '+' : ''}
          {formatNumber(locale, row.delta)}
        </span>
      ),
    },
    {
      id: 'balance',
      header: t('settingsBilling:credits.ledger.column.balance'),
      hideOnMobile: true,
      cell: (row) => (row.runningBalance === null ? '—' : formatNumber(locale, row.runningBalance)),
    },
  ];

  return (
    <section className={cn(styles['card'])}>
      <div className={cn(styles['header'])}>
        <div className={cn(styles['titleRow'])}>
          <h2 className={cn(styles['title'])}>{t('settingsBilling:credits.title')}</h2>
          <Button variant="ghost" size="sm" onClick={() => setInfoOpen(true)}>
            {t('settingsBilling:creditsInfo.trigger')}
          </Button>
        </div>
        <div className={cn(styles['balanceRow'])}>
          <span className={cn(styles['balanceLabel'])}>{t('settingsBilling:credits.balance.label')}</span>
          <span className={cn(styles['balanceValue'])}>{balance.balance === null ? '—' : formatNumber(locale, balance.balance)}</span>
          {balance.zero && <Badge tone="error">{t('settingsBilling:credits.balance.zero')}</Badge>}
          {!balance.zero && balance.low && <Badge tone="warning">{t('settingsBilling:credits.balance.low')}</Badge>}
        </div>
      </div>

      <CreditsInfoModal open={infoOpen} onClose={() => setInfoOpen(false)} onBuyCredits={handleBuyCredits} />

      <h3 className={cn(styles['ledgerTitle'])}>{t('settingsBilling:credits.ledger.title')}</h3>
      <DataTable
        columns={columns}
        rows={ledger.rows}
        rowKey={(row) => row.id}
        query={ledger.query}
        onQueryChange={ledger.onQueryChange}
        label={t('settingsBilling:credits.ledger.title')}
        isLoading={ledger.isLoading}
        isError={ledger.isError}
        onRetry={ledger.onRetry}
        total={ledger.total}
        nextCursor={ledger.nextCursor}
        onLoadMore={ledger.onLoadMore}
        isLoadingMore={ledger.isLoadingMore}
        {...(ledger.loadMoreError ? { loadMoreError: ledger.loadMoreError } : {})}
        emptyTitle={t('settingsBilling:credits.ledger.empty.title')}
        emptyDescription={t('settingsBilling:credits.ledger.empty.description')}
        noResultsTitle={t('settingsBilling:credits.ledger.noResults.title', { query: ledger.query.q ?? '' })}
        onClearFilters={() => {
          const { q: _q, ...rest } = ledger.query;
          ledger.onQueryChange({ ...rest, page: 1 });
        }}
      />
    </section>
  );
}
